import fp from 'lodash/fp';
import React from 'react';
import { Button, Col, Grid, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import Spinner from 'react-spinkit';
import {
  branch,
  lifecycle,
  renderComponent,
  withHandlers,
  withProps,
  withStateHandlers
} from 'recompose';
import { compose } from 'redux';
import { TaskStatus } from '../../../../../constants/tasks';
import { exportBusinessTrainingPlanReportCSV } from '../../../../../reports/export-training-plan-report-pdf';
import {
  defaultQueryTeamMemberConfig,
  responsibleManagerAdded
} from '../../../../../services';
import { currentUserSelector } from '../../../../../state/modules/user';
import { ConfirmationModal, SearchForm } from '../../../../components';
import Loading from '../../../loading';
import TrainingPlanEdit from '../components/training-plan-edit';
import { TrainingPlanOverview } from './training-plan-overview';
import {
  filterNonLegacy,
  filterNonRmGoalCategory
} from '../../../../../common/cpd-category-filter';

const MultipleEntitiesTrainingPlans = ({
  searchQuery,
  onSearchChange,
  loadMore,
  allDataFetched,
  isFetchingEntities,
  isEditingTable,
  setIsEditingTable,
  stateEntityTrainingPlans,
  industryCpdCategorys,
  onSaveUserTrainingPlan,
  onSaveMultiplePlans,
  trainingPlanUpdateConfirmation,
  hideConfirmation,
  hideFailedBadge,
  multipleEntitiesFooter,
  headingCopy,
  isShowingFooter,
  exportPdf,
  overview,
  showOverview,
  toggleOverview
}) => {
  if (showOverview) {
    const exportCSV = () => exportBusinessTrainingPlanReportCSV(overview);
    return (
      <Grid>
        <Row className='no-gutter bottom-gap'>
          <Col xs={12} className='flexbox reverse'>
            <Button onClick={toggleOverview}>Manage</Button>
            <Button className='right-gap' onClick={exportCSV}>
              Export CSV
            </Button>
          </Col>
        </Row>
        <Row className='no-gutter'>
          <Col xs={12}>
            <TrainingPlanOverview {...overview} />
          </Col>
        </Row>
      </Grid>
    );
  }
  return (
    <Grid>
      {headingCopy}
      {overview ? (
        <Row className='no-gutter bottom-gap'>
          <Col xs={12}>
            <Button className='right' onClick={toggleOverview}>
              Overview
            </Button>
          </Col>
        </Row>
      ) : null}
      <Row className='no-gutter'>
        <Col xs={12} id='team-member-search-form'>
          <SearchForm
            disabled={isEditingTable}
            label='Search members'
            searchQuery={searchQuery}
            onSearchChange={onSearchChange}
          />
        </Col>
      </Row>
      <Row className='no-gutter'>
        <Col sm={12}>
          <form>
            <TrainingPlanEdit
              entityTrainingPlans={fp.map((plan) => ({
                ...plan,
                onSave: onSaveUserTrainingPlan
              }))(stateEntityTrainingPlans)}
              emptyDataText='No team members found'
              entityTitle='Team member'
              updateEditingStatus={setIsEditingTable}
              hasMore={!allDataFetched}
              loadMore={loadMore}
              isLoading={isFetchingEntities}
              industryCpdCategorys={industryCpdCategorys}
              onSaveMultiplePlans={onSaveMultiplePlans}
              hideFailedBadge={hideFailedBadge}
              multipleEntitiesFooter={multipleEntitiesFooter}
              isShowingFooter={isShowingFooter}
            />
          </form>
        </Col>
      </Row>

      <Row className='no-gutter submit-row text-center'>
        <Col xs={12} sm={7} smOffset={5}>
          <ConfirmationModal
            id='confirm-update-training-plan'
            show={trainingPlanUpdateConfirmation.show}
            actions={[
              {
                onConfirmAction: () => {
                  const { confirmSave } = trainingPlanUpdateConfirmation.data;
                  confirmSave();
                  hideConfirmation();
                },
                actionLabel: 'Yes'
              }
            ]}
            cancel={hideConfirmation}
            message={
              <span>
                You're about to assign{' '}
                <strong>
                  {fp.join(', ')(
                    fp.get('data.names')(trainingPlanUpdateConfirmation)
                  )}
                </strong>{' '}
                Responsible Manager content as a part of its CPD training plan.
                This content should only be completed by RG105 representatives.
                Do you wish to continue?
              </span>
            }
            headingText={<span>Update Training Plan</span>}
          />
        </Col>
      </Row>
    </Grid>
  );
};

const mapStateToProps = (state) => {
  const cpdCategoryIdsWithRmGoal = state.systemStatus.profile.cpdCategoryGoals
    .filter(({ progressGoalName }) => progressGoalName === 'RM CPD')
    .map(({ cpdCategoryId }) => cpdCategoryId);
  return {
    currentUser: currentUserSelector(state),
    industryCpdCategorys: fp.flow(
      filterNonLegacy,
      filterNonRmGoalCategory(cpdCategoryIdsWithRmGoal)
    )(state.industry.cpdCategorys)
  };
};

const mapDispatchToProps = () => ({});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withProps(({ taskStack }) => ({
    progressingTasks: fp.filter(({ status }) => status === TaskStatus.updating)(
      taskStack
    ),
    initiatedTasks: fp.filter(({ status }) => status === TaskStatus.initiated)(
      taskStack
    ),
    completedTasks: fp.filter(({ status }) => status === TaskStatus.completed)(
      taskStack
    )
  })),
  lifecycle({
    componentDidMount() {
      const { queryEntities } = this.props;
      queryEntities({ take: defaultQueryTeamMemberConfig.take });
    }
  }),
  branch(
    ({ entityQueryResult }) => !entityQueryResult,
    renderComponent(Loading)
  ),
  withProps(({ entityQueryResult, entityTrainingPlans }) => ({
    allDataFetched: entityQueryResult.hits <= entityTrainingPlans.length
  })),
  withStateHandlers(
    {
      filter: {
        skip: 0,
        take: defaultQueryTeamMemberConfig.take
      },
      searchDebounce: undefined,
      isEditingTable: false,
      showOverview: false
    },
    {
      toggleOverview:
        ({ showOverview }) =>
        () => ({
          showOverview: !showOverview
        }),
      updateSearchQuery: () => (filter, debounced) => ({
        filter,
        searchQuery: filter.tags,
        searchDebounce: debounced
      }),
      setIsEditingTable: () => (isEditingTable) => ({
        isEditingTable
      })
    }
  ),
  withHandlers({
    onSearchChange:
      ({ updateSearchQuery, searchDebounce, queryEntities }) =>
      (e) => {
        e.preventDefault();
        e.stopPropagation();

        const filter = {
          skip: 0,
          take: defaultQueryTeamMemberConfig.take,
          tags: e.target.value
        };
        const debounced = fp.debounce(500)(() => {
          queryEntities(filter);
        });
        updateSearchQuery(filter, debounced);
        searchDebounce && searchDebounce.cancel();
        debounced();
      },
    loadMore:
      ({ updateSearchQuery, loadMoreEntities, filter }) =>
      () => {
        const nextfilter = {
          ...filter,
          skip: filter.skip + filter.take
        };
        loadMoreEntities(nextfilter);
        updateSearchQuery(nextfilter, undefined);
      }
  }),
  withStateHandlers(
    ({ entityTrainingPlans }) => ({
      stateEntityTrainingPlans: fp.map((plan) => ({
        ...plan,
        isUpdating: false,
        isSaved: false,
        hasFailed: false
      }))(entityTrainingPlans)
    }),
    {
      reloadTrainingPlans:
        (_, { entityTrainingPlans }) =>
        () => ({
          stateEntityTrainingPlans: fp.map((plan) => ({
            ...plan,
            isUpdating: false
          }))(entityTrainingPlans)
        }),
      toggleUpdating:
        ({ stateEntityTrainingPlans }) =>
        (id) => ({
          stateEntityTrainingPlans: fp.map((plan) =>
            plan.entity.id === id
              ? {
                  ...plan,
                  isUpdating: !plan.isUpdating
                }
              : plan
          )(stateEntityTrainingPlans)
        }),
      showSavedBadge:
        ({ stateEntityTrainingPlans }) =>
        (id) => ({
          stateEntityTrainingPlans: fp.map((plan) =>
            plan.entity.id === id
              ? {
                  ...plan,
                  isSaved: true
                }
              : plan
          )(stateEntityTrainingPlans)
        }),
      showFailedBadge:
        ({ stateEntityTrainingPlans }) =>
        (id) => ({
          stateEntityTrainingPlans: fp.map((plan) =>
            plan.entity.id === id
              ? {
                  ...plan,
                  hasFailed: true
                }
              : plan
          )(stateEntityTrainingPlans)
        }),
      hideSavedBadge:
        ({ stateEntityTrainingPlans }) =>
        (id) => ({
          stateEntityTrainingPlans: fp.map((plan) =>
            plan.entity.id === id
              ? {
                  ...plan,
                  isSaved: false
                }
              : plan
          )(stateEntityTrainingPlans)
        }),
      hideFailedBadge:
        ({ stateEntityTrainingPlans }) =>
        (id) => ({
          stateEntityTrainingPlans: fp.map((plan) =>
            plan.entity.id === id
              ? {
                  ...plan,
                  hasFailed: false
                }
              : plan
          )(stateEntityTrainingPlans)
        })
    }
  ),
  withStateHandlers(
    {
      trainingPlanUpdateConfirmation: {
        show: false,
        data: undefined
      }
    },
    {
      showConfirmation: () => (data) => ({
        trainingPlanUpdateConfirmation: {
          show: true,
          data
        }
      }),
      hideConfirmation: () => () => ({
        trainingPlanUpdateConfirmation: {
          show: false,
          data: undefined
        }
      })
    }
  ),
  lifecycle({
    componentDidUpdate(prevProps) {
      const {
        progressingTasks,
        initiatedTasks,
        completedTasks,
        toggleUpdating,
        initTaskStack,
        stateEntityTrainingPlans,
        reloadTrainingPlans,
        isFetchingEntities,
        showSavedBadge,
        hideSavedBadge,
        showFailedBadge,
        setTrainingPlanSuccess
      } = this.props;

      if (!isFetchingEntities && prevProps.isFetchingEntities) {
        reloadTrainingPlans();
      }

      if (
        setTrainingPlanSuccess &&
        !prevProps.setTrainingPlanSuccess &&
        !setTrainingPlanSuccess.success
      ) {
        showFailedBadge(fp.first(progressingTasks).id);
      }

      if (completedTasks.length === 0 && prevProps.completedTasks.length > 0) {
        fp.forEach(({ id }) => {
          if (
            fp.find(({ isSaved, entity }) => isSaved && entity.id === id)(
              stateEntityTrainingPlans
            )
          ) {
            setTimeout(() => {
              hideSavedBadge(id);
            }, 1000);
          }
        })(prevProps.completedTasks);
      }

      if (
        progressingTasks.length === 0 &&
        initiatedTasks.length > 0 &&
        (!fp.isEqual(completedTasks, prevProps.completedTasks) ||
          completedTasks.length === 0)
      ) {
        fp.first(initiatedTasks).task();
      } else if (completedTasks.length > 0) {
        fp.forEach(({ id }) => {
          if (
            fp.find(({ isUpdating, entity }) => isUpdating && entity.id === id)(
              stateEntityTrainingPlans
            )
          ) {
            toggleUpdating(id);
          }
          if (
            fp.find(
              ({ isSaved, hasFailed, entity }) =>
                !isSaved && !hasFailed && entity.id === id
            )(stateEntityTrainingPlans)
          ) {
            showSavedBadge(id);
          }
        })(completedTasks);
      }

      if (
        completedTasks.length > 0 &&
        progressingTasks.length === 0 &&
        initiatedTasks.length === 0
      ) {
        initTaskStack([], false);
      }
    }
  }),
  withHandlers({
    addSavingTask:
      ({ currentUser, toggleUpdating, saveCpdCategorys, initTaskStack }) =>
      (entity, trainingPlan, industryId, industryEntity, goals, callback) => {
        const task = {
          task: () => {
            saveCpdCategorys(
              trainingPlan,
              entity,
              currentUser.id,
              industryId,
              industryEntity,
              goals
            );
          },
          id: entity.id,
          status: TaskStatus.initiated
        };
        initTaskStack([task], true);
        callback();
        toggleUpdating(entity.id);
      }
  }),
  withHandlers({
    addSavingTasks:
      ({ addSavingTask }) =>
      (trainingPlans, industryId, callback) => {
        fp.forEach(({ entity, trainingPlan, industryEntity, goals }) => {
          addSavingTask(
            entity,
            trainingPlan,
            industryId,
            industryEntity,
            goals,
            () => callback(entity.id)
          );
        })(trainingPlans);
      }
  }),
  withHandlers({
    onSaveUserTrainingPlan:
      ({
        addSavingTask,
        industryCpdCategorys,
        showConfirmation,
        stateEntityTrainingPlans
      }) =>
      ({
        entity,
        trainingPlan,
        industryId,
        industryEntity,
        goals,
        callback
      }) => {
        const prevUserTrainingPlan = fp.find(
          (plan) => fp.get('entity.id')(plan) === entity.id
        )(stateEntityTrainingPlans);
        if (
          responsibleManagerAdded(
            trainingPlan,
            prevUserTrainingPlan.trainingPlan,
            industryCpdCategorys
          )
        ) {
          showConfirmation({
            names: [entity.name],
            confirmSave: () =>
              addSavingTask(
                entity,
                trainingPlan,
                industryId,
                industryEntity,
                goals,
                callback
              )
          });
        } else {
          addSavingTask(
            entity,
            trainingPlan,
            industryId,
            industryEntity,
            goals,
            callback
          );
        }
      },
    onSaveMultiplePlans:
      ({
        addSavingTasks,
        industryCpdCategorys,
        showConfirmation,
        stateEntityTrainingPlans
      }) =>
      ({ plansToUpdate, industryId, callback }) => {
        const plansWithRM = fp.filter(({ entity, trainingPlan }) => {
          const prevUserTrainingPlan = fp.find(
            (plan) => fp.get('entity.id')(plan) === entity.id
          )(stateEntityTrainingPlans);
          return responsibleManagerAdded(
            trainingPlan,
            prevUserTrainingPlan.trainingPlan,
            industryCpdCategorys
          );
        })(plansToUpdate);
        if (plansWithRM.length > 0) {
          showConfirmation({
            names: fp.map(({ entity }) => entity.name)(plansWithRM),
            confirmSave: () =>
              addSavingTasks(plansToUpdate, industryId, callback)
          });
        } else {
          addSavingTasks(plansToUpdate, industryId, callback);
        }
      }
  }),
  withProps(({ stateEntityTrainingPlans }) => ({
    failedCount: fp.flow(
      fp.filter(({ hasFailed }) => hasFailed),
      fp.get('length')
    )(stateEntityTrainingPlans)
  })),
  withHandlers({
    multipleEntitiesFooter:
      ({ taskStack, completedTasks, failedCount }) =>
      (batchSaveTrainingPlans, clearChanges, isEditing) => (
        <div className='fixed-footer'>
          {isEditing || taskStack.length > 0 ? (
            <Button
              className='pull-right'
              onClick={() => batchSaveTrainingPlans()}
              disabled={taskStack.length > 0}
            >
              {taskStack.length > 0 ? (
                <span>
                  <Spinner name='circle' className='spinner' fadeIn='none' />
                  {` ( ${completedTasks.length} / ${taskStack.length} ) updated`}
                </span>
              ) : (
                'Apply Changes'
              )}
            </Button>
          ) : null}
          {failedCount > 0 ? (
            <span className='pull-right badge training-plan-fail'>{` ${failedCount} failed `}</span>
          ) : null}
          {isEditing ? (
            <Button
              className='pull-right'
              bsStyle='plain'
              onClick={() => clearChanges()}
            >
              Clear changes
            </Button>
          ) : null}
        </div>
      )
  }),
  withProps(({ taskStack, failedCount }) => ({
    isShowingFooter: taskStack.length > 0 || failedCount
  }))
)(MultipleEntitiesTrainingPlans);



// WEBPACK FOOTER //
// ./src/containers/pages/app/account-settings/partials/multi-entity-training-plans.js